/**
 * Quiz Save Manager
 * Handles all quiz answer saving with:
 * - Save locking (mutex) to prevent concurrent saves
 * - Request deduplication (AbortController)
 * - State staleness fixes (refs)
 * - Request queue for failed saves
 * - Save verification
 * - Retry logic with exponential backoff
 * - Offline support with IndexedDB (falls back to localStorage)
 * - Token refresh integration
 * - Periodic sync for failed saves
 */

import logger from './logger';
import quizOfflineStorage from './quizOfflineStorage';

class QuizSaveManager {
  constructor(attemptId, quizApiService) {
    this.attemptId = attemptId;
    this.quizApiService = quizApiService;
    
    // Save locking
    this.saveLock = false;
    this.saveQueue = [];
    
    // Request tracking (for deduplication)
    this.inFlightRequests = new Map(); // questionId -> AbortController
    
    // Save status tracking
    this.saveStatus = new Map(); // questionId -> { status: 'saving'|'saved'|'failed', timestamp, error }
    
    // Request queue for failed saves
    this.failedSaveQueue = [];
    this.isProcessingQueue = false;
    
    // State refs (to avoid stale closures)
    this.responsesRef = null;
    this.setResponsesRef = null;
    
    // Debounce timers
    this.debounceTimers = new Map(); // questionId -> timer

    // Timeout timers for stuck 'saving' states
    this.savingTimeouts = new Map(); // questionId -> timeout timer
    
    // Changed questions tracking (only save what changed)
    this.lastSavedState = new Map(); // questionId -> serialized response
    
    // Track saves waiting for token refresh (Priority 4: Concurrent saves)
    this.savesWaitingForRefresh = new Set(); // questionId -> true
    
    // RECOMMENDATION 1: Token refresh retry limit tracking
    this.tokenRefreshRetryCount = new Map(); // questionId -> count
    
    // RECOMMENDATION 6: Circuit breaker pattern
    this.tokenRefreshFailures = 0;
    this.tokenRefreshFailureWindow = null;
    this.CIRCUIT_BREAKER_THRESHOLD = 3; // 3 failures in a row
    this.CIRCUIT_BREAKER_RESET_TIME = 30000; // 30 seconds (reduced from 1 minute for better UX)
    
    // FIX #10: LocalStorage quota management
    this.MAX_FAILED_QUEUE_SIZE = 50; // Maximum number of failed saves to store
    this.MAX_FAILED_QUEUE_AGE = 24 * 60 * 60 * 1000; // 24 hours max age for queued saves
    
    // FIX #5: Token refresh serialization queue
    this.tokenRefreshQueue = []; // Queue of saves waiting for token refresh
    this.isRefreshingToken = false;
    
    // Callbacks
    this.onStatusChange = null;
    
    // FIX #11: Circuit breaker state callback for UI notification
    this.onCircuitBreakerStateChange = null;
    
    // FIX: Re-authentication callback for untimed quizzes
    // Called when token expires and re-authentication is needed
    // This allows the UI to show a re-auth modal instead of auto-submitting
    this.onReAuthRequired = null;
    
    // Track if quiz is timed (affects auto-submit behavior)
    this.isTimedQuiz = false;
    
    // Initialize from localStorage if available
    this.loadFailedQueueFromStorage();
    
    // Process queue on network reconnect
    this.handleOnline = null;
    if (typeof window !== 'undefined') {
      this.handleOnline = () => this.processFailedQueue();
      window.addEventListener('online', this.handleOnline);
    }
    
    // RECOMMENDATION 2: Periodic sync for localStorage queue
    // Sync failed saves every 60 seconds when online
    this.periodicSyncInterval = null;
    this.PERIODIC_SYNC_INTERVAL = 60000; // 60 seconds
  }
  
  /**
   * Start periodic sync for failed saves queue
   * This ensures failed saves are retried even without user interaction
   */
  startPeriodicSync() {
    if (this.periodicSyncInterval) {
      return; // Already running
    }
    
    this.periodicSyncInterval = setInterval(async () => {
      // Only sync if online and have failed saves
      if (typeof navigator !== 'undefined' && navigator.onLine && this.failedSaveQueue.length > 0) {
        logger.debug('Periodic sync: Processing failed saves queue', {
          queueLength: this.failedSaveQueue.length
        });
        await this.processFailedQueue();
      }
    }, this.PERIODIC_SYNC_INTERVAL);
    
    logger.debug('Periodic sync started', { interval: this.PERIODIC_SYNC_INTERVAL });
  }
  
  /**
   * Stop periodic sync
   */
  stopPeriodicSync() {
    if (this.periodicSyncInterval) {
      clearInterval(this.periodicSyncInterval);
      this.periodicSyncInterval = null;
      logger.debug('Periodic sync stopped');
    }
  }
  
  /**
   * Set refs for latest state (fixes stale closure issues)
   */
  setStateRefs(responsesRef, setResponsesRef) {
    this.responsesRef = responsesRef;
    this.setResponsesRef = setResponsesRef;
  }
  
  /**
   * Get latest responses from ref (always current)
   */
  getLatestResponses() {
    return this.responsesRef?.current || {};
  }
  
  /**
   * Serialize response for comparison
   */
  serializeResponse(response) {
    if (!response) return null;
    
    // Normalize the response for consistent comparison
    const normalized = {
      selected_option_id: response.selected_option_id || null,
      answer_text: response.answer_text || null,
      selected_option_ids: response.selected_option_ids || null,
      answer_texts: response.answer_texts || null
    };
    
    // Normalize arrays (sort for consistent comparison)
    if (normalized.selected_option_ids && Array.isArray(normalized.selected_option_ids)) {
      normalized.selected_option_ids = [...normalized.selected_option_ids].sort((a, b) => a - b);
    }
    if (normalized.answer_texts && Array.isArray(normalized.answer_texts)) {
      normalized.answer_texts = [...normalized.answer_texts];
    }
    
    // Normalize answer_text (trim whitespace, handle empty strings)
    if (normalized.answer_text && typeof normalized.answer_text === 'string') {
      normalized.answer_text = normalized.answer_text.trim();
      if (normalized.answer_text === '') {
        normalized.answer_text = null;
      }
    }
    
    return JSON.stringify(normalized);
  }
  
  /**
   * Normalize response string for comparison (handles JSON formatting differences)
   */
  normalizeResponseString(serialized) {
    if (!serialized) return '';
    try {
      const parsed = JSON.parse(serialized);
      // Re-serialize to ensure consistent formatting
      return JSON.stringify(parsed);
    } catch {
      // Not JSON, return as-is (trimmed)
      return serialized.trim();
    }
  }
  
  /**
   * Check if response has changed since last save
   * IMPROVED: Better handling of edge cases and normalization
   */
  hasResponseChanged(questionId, response) {
    // If no previous save, consider it changed
    const lastSaved = this.lastSavedState.get(questionId);
    if (!lastSaved) {
      return true;
    }
    
    // Serialize current response
    const serialized = this.serializeResponse(response);
    if (!serialized) {
      // If current response is null/empty but we had a saved response, it changed
      return lastSaved !== null;
    }
    
    // Normalize both for comparison
    const normalizedCurrent = this.normalizeResponseString(serialized);
    const normalizedLast = this.normalizeResponseString(lastSaved);
    
    return normalizedCurrent !== normalizedLast;
  }
  
  /**
   * Mark response as saved
   */
  markResponseSaved(questionId, response) {
    this.lastSavedState.set(questionId, this.serializeResponse(response));
    this.updateSaveStatus(questionId, 'saved', null);
  }
  
  /**
   * Update save status for a question
   */
  updateSaveStatus(questionId, status, error = null) {
    this.saveStatus.set(questionId, {
      status,
      timestamp: Date.now(),
      error: error?.message || error
    });
    
    // Notify listeners if callback is set
    if (this.onStatusChange) {
      this.onStatusChange(questionId, status, error);
    }
  }
  
  /**
   * Get save status for a question
   */
  getSaveStatus(questionId) {
    return this.saveStatus.get(questionId) || { status: 'unsaved', timestamp: null, error: null };
  }
  
  /**
   * Cancel in-flight request for a question
   */
  cancelInFlightRequest(questionId) {
    const controller = this.inFlightRequests.get(questionId);
    if (controller) {
      controller.abort();
      this.inFlightRequests.delete(questionId);
    }
  }
  
  /**
   * Save a single response with all protections
   */
  async saveResponse(questionId, response, options = {}) {
    const {
      retryCount = 0,
      maxRetries = 3,
      skipVerification = false,
      skipDeduplication = false,
      tokenRefreshDepth = 0  // RECOMMENDATION 2: Track token refresh recursion depth
    } = options;

    // DEBUG: Log save attempt
    logger.debug('SaveManager: Save attempt', {
      questionId,
      retryCount,
      maxRetries,
      hasChanged: this.hasResponseChanged(questionId, response),
      tokenRefreshDepth,
      timestamp: Date.now()
    });

    // Check if response has changed
    if (!this.hasResponseChanged(questionId, response)) {
      logger.debug('Question unchanged, skipping save', { questionId });
      // FIX: Ensure status is not stuck in 'saving' if we skip
      const currentStatus = this.saveStatus.get(questionId);
      if (currentStatus?.status === 'saving') {
        this.updateSaveStatus(questionId, 'saved', null);
      }
      return { success: true, skipped: true };
    }
    
    // Cancel any in-flight request for this question (deduplication)
    if (!skipDeduplication) {
      const hadInFlight = this.inFlightRequests.has(questionId);
      this.cancelInFlightRequest(questionId);
      if (hadInFlight) {
        logger.debug('Cancelled in-flight save for question (deduplication)', { questionId });
      }
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    this.inFlightRequests.set(questionId, abortController);
    
    // Update status to saving
    this.updateSaveStatus(questionId, 'saving');
    
    // FIX #3: Improved timeout handling for stuck 'saving' states
    // Set timeout to clear stuck 'saving' states (30 seconds)
    // If save doesn't complete within 30 seconds, verify if it actually saved before marking as failed
    const SAVING_TIMEOUT = 30000; // 30 seconds
    const saveStartTime = Date.now();
    const timeoutId = setTimeout(async () => {
      const currentStatus = this.saveStatus.get(questionId);
      // Only clear if still in 'saving' state (save didn't complete)
      if (currentStatus && currentStatus.status === 'saving') {
        logger.warn('Save timeout for question - checking if save actually persisted', { questionId });
        
        // FIX #3: Before marking as failed, verify if the save actually succeeded
        // This prevents false negatives when network is slow but save eventually succeeds
        try {
          const verified = await this.verifySave(questionId, response);
          if (verified) {
            logger.info('Save timeout but verification succeeded - marking as saved', { questionId });
            this.markResponseSaved(questionId, response);
            this.inFlightRequests.delete(questionId);
            return;
          }
        } catch (verifyError) {
          logger.debug('Verification failed during timeout check', { questionId, error: verifyError });
        }
        
        // Save didn't persist, mark as failed
        this.updateSaveStatus(questionId, 'failed', {
          message: 'Save timed out. Please try saving again.',
          timeout: true,
          duration: Date.now() - saveStartTime
        });
        this.inFlightRequests.delete(questionId);
        
        // Add to failed queue for retry
        this.addToFailedQueue(questionId, response, { message: 'Save timed out', timeout: true });
      }
      this.savingTimeouts.delete(questionId);
    }, SAVING_TIMEOUT);
    this.savingTimeouts.set(questionId, timeoutId);
    
    try {
      // Prepare answer_text
      // CRITICAL FIX: For True/False questions, answer_text should be null or omitted (not empty string)
      // The validation rejects empty strings but allows null or missing fields
      let answerText = response.answer_text;
      
      // Handle multiple choice - convert selected_option_ids to JSON if needed
      if (!answerText && response.selected_option_ids && Array.isArray(response.selected_option_ids) && response.selected_option_ids.length > 0) {
        answerText = JSON.stringify(response.selected_option_ids);
      }
      
      // For True/False or single-choice questions with selected_option_id but no answer_text,
      // omit answer_text entirely (validation allows optional field to be missing)
      // Only include answer_text if it has a value
      const requestBody = {
        question_id: parseInt(questionId),
        selected_option_id: response.selected_option_id
      };
      
      // Only include answer_text if it has a non-empty value
      // This prevents validation errors for True/False questions
      if (answerText !== null && answerText !== undefined && answerText !== '') {
        requestBody.answer_text = answerText;
      }
      
      // Make save request
      const savePromise = this.quizApiService.saveResponse(this.attemptId, requestBody);
      
      // Check if aborted
      if (abortController.signal.aborted) {
        throw new Error('Save cancelled (newer save in progress)');
      }
      
      const result = await savePromise;
      
      // Check if aborted after request completes
      // PRIORITY 1 FIX: Don't abort if token expiration - we'll retry
      // Only check abort for non-token-expiration errors
      if (abortController.signal.aborted) {
        // Check if the result indicates token expiration
        const isTokenError = result && (
          result.error?.includes('expired') || 
          result.error?.includes('Token expired') ||
          result.status === 401
        );
        
        if (!isTokenError) {
          throw new Error('Save cancelled (newer save in progress)');
        }
        // If token expiration, continue to error handling (will retry)
      }
      
      // Verify save if requested
      if (!skipVerification) {
        const verified = await this.verifySave(questionId, response);
        if (!verified) {
          throw new Error('Save verification failed');
        }
      }
      
      // Mark as saved
      this.markResponseSaved(questionId, response);
      this.inFlightRequests.delete(questionId);
      
      // Clear saving timeout since save completed successfully
      const timeoutId = this.savingTimeouts.get(questionId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.savingTimeouts.delete(questionId);
      }
      
      logger.debug('Successfully saved question', { questionId });
      return { success: true, result };
      
    } catch (error) {
      // PRIORITY 1 FIX: Don't abort if token expiration - we'll retry
      // Only delete in-flight request if it's not a token expiration error
      // (token expiration errors will be retried, so we want to keep the abort controller)
      const isTokenExpiration = this.isTokenExpirationError(error);
      
      // FIX: Check for cancelled saves FIRST before logging as error
      // Cancelled saves are expected behavior (deduplication), not errors
      if (error.message?.includes('cancelled')) {
        logger.debug('Save cancelled for question (newer save in progress)', { questionId });
        
        // Clear saving timeout
        const timeoutId = this.savingTimeouts.get(questionId);
        if (timeoutId) {
          clearTimeout(timeoutId);
          this.savingTimeouts.delete(questionId);
        }
        
        // Don't delete in-flight request - the newer save is handling it
        return { success: false, cancelled: true };
      }
      
      // CRITICAL FIX: Handle 404 errors immediately (non-retryable)
      // 404 means the attempt doesn't exist or route is wrong - don't retry
      if (error?.status === 404) {
        logger.error('404 error saving question: Attempt not found or invalid route', { questionId });
        this.inFlightRequests.delete(questionId);
        
        // Clear saving timeout
        const timeoutId = this.savingTimeouts.get(questionId);
        if (timeoutId) {
          clearTimeout(timeoutId);
          this.savingTimeouts.delete(questionId);
        }
        
        const notFoundError = new Error('Quiz attempt not found. The quiz may have been deleted or you may need to refresh the page.');
        notFoundError.status = 404;
        notFoundError.notFound = true;
        
        // Update status to failed immediately to clear 'saving' state
        this.updateSaveStatus(questionId, 'failed', notFoundError);
        
        // Don't add to failed queue - 404 is not retryable
        return { success: false, error: notFoundError, notFound: true };
      }
      
      // CRITICAL FIX: Handle 403 errors immediately (non-retryable for non-CSRF errors)
      // 403 can mean authentication failed, role mismatch, or CSRF token issue
      if (error?.status === 403) {
        // Check if it's a CSRF error (handled separately below)
        const isCSRFError = error?.csrfTokenMissing || error?.message?.includes('CSRF');
        
        if (!isCSRFError) {
          // Non-CSRF 403 error - likely authentication/authorization issue
          logger.error('403 Forbidden error saving question', { questionId, error: error.message || 'Authentication or authorization failed' });
          this.inFlightRequests.delete(questionId);
          
          // Clear saving timeout
          const timeoutId = this.savingTimeouts.get(questionId);
          if (timeoutId) {
            clearTimeout(timeoutId);
            this.savingTimeouts.delete(questionId);
          }
          
          const forbiddenError = new Error('Access denied. Please refresh the page and try again, or contact support if the issue persists.');
          forbiddenError.status = 403;
          forbiddenError.forbidden = true;
          
          // Update status to failed immediately to clear 'saving' state
          this.updateSaveStatus(questionId, 'failed', forbiddenError);
          
          // Don't retry 403 errors (except CSRF which is handled below)
          return { success: false, error: forbiddenError, forbidden: true };
        }
        // If it's a CSRF error, fall through to CSRF handling below
      }
      
      // Handle CSRF token missing errors
      const isCSRFError = error?.csrfTokenMissing || 
                         (error?.status === 403 && error?.message?.includes('CSRF'));
      
      if (isCSRFError && retryCount < maxRetries) {
        logger.debug('CSRF token missing, retrying', { attempt: retryCount + 1, maxRetries, questionId });
        // Wait a bit before retry to allow CSRF token fetch to complete
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        // Retry with same options but increment retry count
        return this.saveResponse(questionId, response, {
          ...options,
          retryCount: retryCount + 1
        });
      }
      
      // Handle token expiration
      if (isTokenExpiration && retryCount === 0) {
        // RECOMMENDATION 1: Check token refresh retry limit
        const tokenRefreshAttempts = this.tokenRefreshRetryCount.get(questionId) || 0;
        const MAX_TOKEN_REFRESH_RETRIES = 2; // Max 2 token refresh cycles per question
        
        if (tokenRefreshAttempts >= MAX_TOKEN_REFRESH_RETRIES) {
          logger.error('Max token refresh retries reached for question', { questionId });
          this.tokenRefreshRetryCount.delete(questionId);
          this.inFlightRequests.delete(questionId);
          this.savesWaitingForRefresh.delete(questionId);
          
          const error = new Error('Session expired multiple times. Please refresh the page and log in again.');
          error.refreshTokenExpired = true;
          this.updateSaveStatus(questionId, 'failed', error);
          this.addToFailedQueue(questionId, response, error);
          return { success: false, error };
        }
        
        // RECOMMENDATION 2: Check token refresh depth
        const MAX_TOKEN_REFRESH_DEPTH = 2;
        if (tokenRefreshDepth >= MAX_TOKEN_REFRESH_DEPTH) {
          logger.error('Max token refresh depth reached for question', { questionId });
          this.tokenRefreshRetryCount.delete(questionId);
          this.inFlightRequests.delete(questionId);
          this.savesWaitingForRefresh.delete(questionId);
          
          const error = new Error('Max token refresh depth exceeded. Please refresh the page and log in again.');
          error.refreshTokenExpired = true;
          this.updateSaveStatus(questionId, 'failed', error);
          this.addToFailedQueue(questionId, response, error);
          return { success: false, error };
        }
        
        logger.debug('Token expired, refreshing and retrying question', { 
          questionId, 
          attempt: tokenRefreshAttempts + 1, 
          maxAttempts: MAX_TOKEN_REFRESH_RETRIES, 
          depth: tokenRefreshDepth 
        });
        
        // Increment token refresh attempt count
        this.tokenRefreshRetryCount.set(questionId, tokenRefreshAttempts + 1);
        
        // PRIORITY 4: Track this save as waiting for refresh
        this.savesWaitingForRefresh.add(questionId);
        
        try {
          // Dynamically import token refresh manager to avoid circular dependencies
          const tokenRefreshManager = (await import('./tokenRefreshManager')).default;
          
          // Check if refresh token is available
          if (!tokenRefreshManager.hasRefreshToken()) {
            const refreshError = new Error('Your session has expired. Please log in again.');
            refreshError.refreshTokenExpired = true;
            
            // FIX: For untimed quizzes, save locally and DON'T auto-submit
            // Only show re-auth modal if refresh token is truly expired (not just temporarily unavailable)
            if (!this.isTimedQuiz) {
              logger.info('Refresh token unavailable for untimed quiz - saving locally and continuing', {
                attemptId: this.attemptId,
                questionId
              });
              
              // Save to local storage for later sync
              await this.addToFailedQueue(questionId, response, refreshError);
              
              // Check if refresh token is TRULY expired (not just missing temporarily)
              // Only trigger re-auth modal if the token has been expired for a while
              const isRefreshTokenTrulyExpired = tokenRefreshManager.isRefreshTokenExpired();
              
              if (isRefreshTokenTrulyExpired && this.onReAuthRequired) {
                // Refresh token is truly expired (7+ days) - show re-auth modal
                logger.warn('Refresh token truly expired - triggering re-auth modal', {
                  attemptId: this.attemptId
                });
                await this.triggerReAuthRequired(refreshError);
              }
              
              // Return success=false but DON'T throw - quiz continues
              return {
                success: false,
                savedLocally: true,
                reAuthRequired: isRefreshTokenTrulyExpired,
                error: 'Saved locally - will sync when connection restored'
              };
            }
            
            // FIX: For timed quizzes, also save locally before throwing
            // This ensures the answer is preserved even if the quiz auto-submits
            logger.info('Refresh token unavailable for timed quiz - saving locally before error', {
              attemptId: this.attemptId,
              questionId
            });
            await this.addToFailedQueue(questionId, response, refreshError);
            
            throw refreshError;
          }
          
          // FIX #11: Improved circuit breaker with better UX
          const now = Date.now();
          if (this.tokenRefreshFailureWindow &&
              (now - this.tokenRefreshFailureWindow) <= this.CIRCUIT_BREAKER_RESET_TIME &&
              this.tokenRefreshFailures >= this.CIRCUIT_BREAKER_THRESHOLD) {
            const timeRemaining = Math.ceil((this.CIRCUIT_BREAKER_RESET_TIME - (now - this.tokenRefreshFailureWindow)) / 1000);
            logger.warn('Token refresh circuit breaker opened - too many failures', {
              failures: this.tokenRefreshFailures,
              timeRemaining
            });
            
            // FIX #11: Notify UI about circuit breaker state
            if (this.onCircuitBreakerStateChange) {
              this.onCircuitBreakerStateChange({
                isOpen: true,
                failures: this.tokenRefreshFailures,
                timeRemaining,
                canManualReset: true
              });
            }
            
            const circuitError = new Error(`Too many login attempts failed. Please wait ${timeRemaining} seconds or refresh the page.`);
            circuitError.refreshTokenExpired = true;
            circuitError.circuitBreakerOpen = true;
            circuitError.timeRemaining = timeRemaining;
            throw circuitError;
          }
          
          // RECOMMENDATION 5: Add timeout for token refresh (10 seconds)
          const TOKEN_REFRESH_TIMEOUT = 10000;
          
          // Wait for any in-progress refresh or perform new refresh
          const refreshPromise = tokenRefreshManager.isRefreshInProgress()
            ? tokenRefreshManager.waitForRefresh()
            : (async () => {
                const apiService = (await import('../services/api')).default;
                return tokenRefreshManager.refreshTokenWithLock(apiService.baseURL);
              })();
          
          // Create timeout promise
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Token refresh timed out')), TOKEN_REFRESH_TIMEOUT);
          });
          
          // Race between refresh and timeout
          await Promise.race([refreshPromise, timeoutPromise]);
          
          // Reset circuit breaker on successful refresh
          this.tokenRefreshFailures = 0;
          this.tokenRefreshFailureWindow = null;
          
          // Small delay to ensure token is updated
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // PRIORITY 4: After refresh completes, process all waiting saves with latest data
          // This ensures concurrent saves all get the latest response data
          const waitingSaves = Array.from(this.savesWaitingForRefresh);
          this.savesWaitingForRefresh.clear();
          
          // Get latest responses once for all waiting saves
          const latestResponses = this.getLatestResponses();
          
          // Process concurrent saves (other questions) asynchronously
          const concurrentSaves = waitingSaves.filter(id => id !== questionId);
          for (const waitingQuestionId of concurrentSaves) {
            const latestResponse = latestResponses[waitingQuestionId];
            
            if (!latestResponse) {
              logger.debug('Response for question no longer exists, skipping retry', { questionId: waitingQuestionId });
              this.inFlightRequests.delete(waitingQuestionId);
              continue;
            }
            
            if (!this.hasResponseChanged(waitingQuestionId, latestResponse)) {
              logger.debug('Response for question unchanged after refresh, skipping retry', { questionId: waitingQuestionId });
              this.inFlightRequests.delete(waitingQuestionId);
              continue;
            }
            
            // RECOMMENDATION 4: Retry concurrent saves asynchronously with tokenRefreshDepth: 1
            // This prevents them from triggering another refresh
            logger.debug('Retrying concurrent save for question after token refresh', { questionId: waitingQuestionId });
            this.saveResponse(waitingQuestionId, latestResponse, { 
              skipVerification: true, 
              maxRetries: 1,
              retryCount: 0, 
              skipDeduplication: true,
              tokenRefreshDepth: 1  // Prevent them from triggering another refresh
            }).catch(err => {
              logger.error('Concurrent retry failed for question', { questionId: waitingQuestionId, error: err });
              this.inFlightRequests.delete(waitingQuestionId);
              this.tokenRefreshRetryCount.delete(waitingQuestionId);
              
              // Update status
              this.updateSaveStatus(waitingQuestionId, 'failed', err);
              
              // Add to failed queue if it's a token expiration error
              if (this.isTokenExpirationError(err)) {
                const latestResponses = this.getLatestResponses();
                const latestResponse = latestResponses[waitingQuestionId];
                if (latestResponse) {
                  this.addToFailedQueue(waitingQuestionId, latestResponse, err);
                }
              }
            });
          }
          
          // For the current save, get latest response and retry synchronously (await)
          const latestResponse = latestResponses[questionId];
          
          if (!latestResponse) {
            // Response was cleared/changed - don't retry
            logger.debug('Response for question no longer exists, skipping retry', { questionId });
            this.inFlightRequests.delete(questionId);
            this.savesWaitingForRefresh.delete(questionId);
            return { success: false, cancelled: true, reason: 'Response no longer exists' };
          }
          
          // Check if response has changed
          if (!this.hasResponseChanged(questionId, latestResponse)) {
            // Response hasn't changed - already saved
            logger.debug('Response for question unchanged after refresh, skipping retry', { questionId });
            this.inFlightRequests.delete(questionId);
            this.savesWaitingForRefresh.delete(questionId);
            return { success: true, skipped: true, reason: 'Response unchanged' };
          }
          
          // Retry save with LATEST response (await to get actual result)
          logger.debug('Retrying save for question with latest response after token refresh', { questionId });
          this.inFlightRequests.delete(questionId); // Clear old abort controller
          
          // Retry with latest response and await the result
          // RECOMMENDATION 2: Increment tokenRefreshDepth to track recursion
          const retryResult = await this.saveResponse(questionId, latestResponse, { 
            ...options, 
            retryCount: 0, 
            skipDeduplication: true,
            tokenRefreshDepth: tokenRefreshDepth + 1  // Increment depth
          });
          
          // RECOMMENDATION 1: Clear token refresh retry count on successful retry
          if (retryResult.success) {
            this.tokenRefreshRetryCount.delete(questionId);
          }
          
          return retryResult;
          
        } catch (refreshError) {
          // RECOMMENDATION 3: Get all waiting saves before clearing
          const allWaitingSaves = Array.from(this.savesWaitingForRefresh);
          
          // Clear the set
          this.savesWaitingForRefresh.clear();
          
          // RECOMMENDATION 6: Track token refresh failures for circuit breaker
          if (refreshError.refreshTokenExpired || refreshError.noRefreshToken || 
              refreshError.message?.includes('expired') || 
              refreshError.message?.includes('timed out')) {
            const now = Date.now();
            if (!this.tokenRefreshFailureWindow || 
                (now - this.tokenRefreshFailureWindow) > this.CIRCUIT_BREAKER_RESET_TIME) {
              // Reset window
              this.tokenRefreshFailureWindow = now;
              this.tokenRefreshFailures = 1;
            } else {
              this.tokenRefreshFailures++;
            }
          }
          
          // RECOMMENDATION 3: Clean up all waiting saves, not just the current one
          const errorMessage = refreshError.refreshTokenExpired || refreshError.noRefreshToken
            ? 'Your session has expired. Please refresh the page and log in again.'
            : refreshError.message || 'Failed to refresh session. Please try again.';
          
          for (const waitingQuestionId of allWaitingSaves) {
            this.inFlightRequests.delete(waitingQuestionId);
            this.tokenRefreshRetryCount.delete(waitingQuestionId);
            
            // Update status for all failed saves
            this.updateSaveStatus(waitingQuestionId, 'failed', {
              message: errorMessage,
              refreshTokenExpired: refreshError.refreshTokenExpired || refreshError.noRefreshToken,
              timestamp: Date.now()
            });
            
            // Add to failed queue
            const latestResponses = this.getLatestResponses();
            const latestResponse = latestResponses[waitingQuestionId];
            if (latestResponse) {
              this.addToFailedQueue(waitingQuestionId, latestResponse, {
                message: errorMessage,
                refreshTokenExpired: refreshError.refreshTokenExpired || refreshError.noRefreshToken
              });
            }
            
            // Notify UI for each failed save
            if (this.onStatusChange) {
              this.onStatusChange(waitingQuestionId, 'failed', {
                message: errorMessage,
                refreshTokenExpired: refreshError.refreshTokenExpired || refreshError.noRefreshToken,
                action: (refreshError.refreshTokenExpired || refreshError.noRefreshToken) ? 'refresh_page' : 'retry'
              });
            }
          }
          
          // PRIORITY 3: Better error visibility
          // Return error for current save
          if (refreshError.refreshTokenExpired || refreshError.noRefreshToken) {
            // FIX: For untimed quizzes, save locally and continue - DON'T auto-submit
            if (!this.isTimedQuiz) {
              logger.info('Token refresh failed for untimed quiz - saving locally and continuing', {
                attemptId: this.attemptId,
                error: errorMessage
              });
              
              // Save to local storage for later sync
              await this.addToFailedQueue(questionId, response, refreshError);
              
              // Only trigger re-auth modal if refresh token is TRULY expired (7+ days)
              // For temporary failures, just save locally and keep retrying
              const tokenRefreshManager = (await import('./tokenRefreshManager')).default;
              const isRefreshTokenTrulyExpired = tokenRefreshManager.isRefreshTokenExpired();
              
              if (isRefreshTokenTrulyExpired && this.onReAuthRequired) {
                logger.warn('Refresh token truly expired - triggering re-auth modal', {
                  attemptId: this.attemptId
                });
                await this.triggerReAuthRequired(refreshError);
              }
              
              // Return success=false but DON'T throw - quiz continues
              return {
                success: false,
                savedLocally: true,
                reAuthRequired: isRefreshTokenTrulyExpired,
                error: 'Saved locally - will sync when connection restored'
              };
            }
            
            // FIX: For timed quizzes, also save locally to prevent data loss
            // The quiz will still auto-submit when timer expires, but at least the answer is preserved
            logger.info('Token refresh failed for timed quiz - saving locally', {
              attemptId: this.attemptId,
              error: errorMessage
            });
            await this.addToFailedQueue(questionId, response, refreshError);
            
            return { success: false, error: errorMessage, refreshTokenExpired: true, savedLocally: true };
          }
          
          if (refreshError.rateLimited) {
            // Rate limited - retry after delay
            const delay = 5000; // 5 seconds
            logger.debug('Token refresh rate limited, retrying save', { delay, questionId });
            await new Promise(resolve => setTimeout(resolve, delay));
            
            // Get latest response before retrying (user might have changed answer during delay)
            const latestResponses = this.getLatestResponses();
            const latestResponse = latestResponses[questionId] || response; // Fallback to original if not found
            
            return this.saveResponse(questionId, latestResponse, { 
              ...options, 
              retryCount: 0, 
              skipDeduplication: true,
              tokenRefreshDepth: tokenRefreshDepth + 1  // Increment depth for retry
            });
          }
          
          // Other refresh errors - fall through to retry logic
          logger.error('Token refresh failed', { error: refreshError, questionId });
          return { success: false, error: errorMessage };
        }
      }
      
      // If we get here and it was a token expiration error, clean up
      if (isTokenExpiration) {
        this.inFlightRequests.delete(questionId);
        this.savesWaitingForRefresh.delete(questionId);
        // RECOMMENDATION 1: Also clear token refresh retry count if we're not retrying
        if (retryCount >= maxRetries) {
          this.tokenRefreshRetryCount.delete(questionId);
        }
      }
      
      // Retry with exponential backoff
      if (retryCount < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Max 10s
        logger.debug('Retrying save for question', { questionId, delay, attempt: retryCount + 1, maxRetries });
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.saveResponse(questionId, response, { ...options, retryCount: retryCount + 1 });
      }
      
      // Max retries reached - add to failed queue
      // Enhance error message based on error type
      const enhancedError = this.enhanceErrorMessage(error);
      
      // Clear saving timeout
      const timeoutId = this.savingTimeouts.get(questionId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.savingTimeouts.delete(questionId);
      }
      
      this.updateSaveStatus(questionId, 'failed', enhancedError);
      this.addToFailedQueue(questionId, response, enhancedError);
      
      logger.error('Failed to save question after max retries', { questionId, maxRetries, error });
      return { success: false, error: enhancedError };
    }
  }
  
  /**
   * Save multiple responses with locking
   * Handles partial saves when token expires mid-batch
   */
  async saveAllResponses(responses, options = {}) {
    const {
      skipUnchanged = true,
      skipVerification = false,
      onProgress = null
    } = options;
    
    // Wait for lock
    await this.acquireLock();
    
    try {
      const responsesToSave = skipUnchanged
        ? Object.entries(responses).filter(([qId, resp]) => 
            resp && this.hasResponseChanged(qId, resp)
          )
        : Object.entries(responses).filter(([qId, resp]) => resp);
      
      if (responsesToSave.length === 0) {
        logger.debug('No changed responses to save');
        return { success: true, saved: 0, skipped: Object.keys(responses).length };
      }
      
      logger.debug('Saving responses', { count: responsesToSave.length });
      
      const results = [];
      const savedQuestionIds = new Set(); // Track successfully saved questions
      let tokenExpiredDuringBatch = false;
      
      for (const [questionId, response] of responsesToSave) {
        // Skip if already saved successfully
        if (savedQuestionIds.has(questionId)) {
          continue;
        }
        
        const result = await this.saveResponse(questionId, response, {
          skipVerification,
          skipDeduplication: false
        });
        
        results.push({ questionId, ...result });
        
        if (result.success) {
          savedQuestionIds.add(questionId);
        } else if (result.refreshTokenExpired) {
          // Token expired - stop batch, but don't fail already-saved questions
          tokenExpiredDuringBatch = true;
          logger.warn('Token expired during batch save', { saved: savedQuestionIds.size, total: responsesToSave.length });
          
          // Try to refresh token and continue with remaining questions
          try {
            const tokenRefreshManager = (await import('./tokenRefreshManager')).default;
            if (tokenRefreshManager.hasRefreshToken() && !tokenRefreshManager.isRefreshInProgress()) {
              const apiService = (await import('../services/api')).default;
              await tokenRefreshManager.refreshTokenWithLock(apiService.baseURL);
              await new Promise(resolve => setTimeout(resolve, 200));
              // Continue with remaining questions
            } else {
              // Cannot refresh - break and return partial results
              break;
            }
          } catch (refreshError) {
            // Refresh failed - break and return partial results
            logger.error('Failed to refresh token during batch save', { error: refreshError });
            break;
          }
        }
        
        if (onProgress) {
          onProgress(questionId, result.success);
        }
      }
      
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success && !r.cancelled && !r.skipped).length;
      const refreshTokenExpired = results.some(r => r.refreshTokenExpired);
      
      return {
        success: failed === 0 && !tokenExpiredDuringBatch,
        saved: successful,
        failed,
        skipped: results.filter(r => r.skipped).length,
        refreshTokenExpired,
        partialSave: tokenExpiredDuringBatch,
        savedQuestionIds: Array.from(savedQuestionIds),
        results
      };
      
    } finally {
      this.releaseLock();
    }
  }
  
  /**
   * Acquire save lock (mutex)
   */
  async acquireLock() {
    if (!this.saveLock) {
      this.saveLock = true;
      return;
    }
    
    // Wait for lock
    return new Promise((resolve) => {
      this.saveQueue.push(resolve);
    });
  }
  
  /**
   * Release save lock
   */
  releaseLock() {
    this.saveLock = false;
    if (this.saveQueue.length > 0) {
      const next = this.saveQueue.shift();
      this.saveLock = true;
      next();
    }
  }
  
  /**
   * Verify that save persisted correctly
   * Skips verification if token expired (assumes success to avoid false negatives)
   */
  async verifySave(questionId, expectedResponse) {
    try {
      // Fetch attempt to get saved responses
      const attemptData = await this.quizApiService.getAttempt(this.attemptId);
      
      if (!attemptData.success || !attemptData.data?.existing_responses) {
        logger.warn('Could not verify save for question: No attempt data', { questionId });
        return true; // Assume success if we can't verify
      }
      
      const savedResponse = attemptData.data.existing_responses.find(
        r => r.question_id === parseInt(questionId)
      );
      
      if (!savedResponse) {
        logger.warn('Could not verify save for question: Response not found', { questionId });
        return false;
      }
      
      // Compare saved vs expected
      // IMPORTANT: normalize expected answer_text the same way saveResponse() prepares payload.
      let expectedAnswerText = expectedResponse.answer_text;
      if (!expectedAnswerText && expectedResponse.selected_option_ids && Array.isArray(expectedResponse.selected_option_ids) && expectedResponse.selected_option_ids.length > 0) {
        expectedAnswerText = JSON.stringify(expectedResponse.selected_option_ids);
      }

      const normalizedSavedSelectedOptionId =
        savedResponse.selected_option_id === null || savedResponse.selected_option_id === undefined
          ? null
          : parseInt(savedResponse.selected_option_id);

      const normalizedExpectedSelectedOptionId =
        expectedResponse.selected_option_id === null || expectedResponse.selected_option_id === undefined
          ? null
          : parseInt(expectedResponse.selected_option_id);

      const normalizedSavedAnswerText = savedResponse.answer_text || '';
      const normalizedExpectedAnswerText = expectedAnswerText || '';

      const savedMatches =
        normalizedSavedSelectedOptionId === normalizedExpectedSelectedOptionId &&
        normalizedSavedAnswerText === normalizedExpectedAnswerText;
      
      if (!savedMatches) {
        logger.error('Verification failed for question', { questionId, expected: expectedResponse, saved: savedResponse });
        return false;
      }
      
      return true;
    } catch (error) {
      // If verification fails due to token expiration, assume success (don't retry save)
      if (this.isTokenExpirationError(error)) {
        logger.warn('Verification skipped for question due to token expiration (assuming success)', { questionId });
        return true; // Assume success to avoid false negatives
      }
      
      logger.warn('Verification error for question', { questionId, error });
      return true; // Assume success if verification fails (network issue)
    }
  }
  
  /**
   * Add failed save to queue
   * RECOMMENDATION 4: Uses IndexedDB for more reliable offline storage
   */
  addToFailedQueue(questionId, response, error = null) {
    const queueItem = {
      questionId,
      response,
      attemptId: this.attemptId,
      timestamp: Date.now(),
      retryCount: 0,
      error: error?.message || error
    };

    this.failedSaveQueue.push(queueItem);
    
    // Save to both localStorage (for backward compatibility) and IndexedDB (for reliability)
    this.saveFailedQueueToStorage();
    
    // RECOMMENDATION 4: Also save to IndexedDB for better reliability
    quizOfflineStorage.saveFailedSave(this.attemptId, questionId, response, error)
      .catch(err => logger.warn('Failed to save to IndexedDB', { error: err }));

    // DEBUG: Log failed save queuing
    logger.warn('Save added to failed queue', {
      questionId,
      queueLength: this.failedSaveQueue.length,
      error: error?.message || error,
      timestamp: Date.now()
    });
  }
  
  /**
   * Process failed save queue
   */
  async processFailedQueue() {
    if (this.isProcessingQueue || this.failedSaveQueue.length === 0) {
      logger.debug('Failed queue processing skipped', {
        isProcessing: this.isProcessingQueue,
        queueLength: this.failedSaveQueue.length
      });
      return;
    }

    this.isProcessingQueue = true;
    logger.debug('Processing failed saves', {
      count: this.failedSaveQueue.length,
      hasRefreshToken: typeof window !== 'undefined' ? !!localStorage.getItem('refresh_token') : 'N/A'
    });
    
    try {
      // Check refresh token before processing queue
      const tokenRefreshManager = (await import('./tokenRefreshManager')).default;
      if (!tokenRefreshManager.hasRefreshToken()) {
        logger.warn('Cannot process failed queue: Refresh token expired');
        // Update status for all failed saves
        for (const item of this.failedSaveQueue) {
          this.updateSaveStatus(item.questionId, 'failed', {
            message: 'Session expired. Please refresh the page and log in again.',
            refreshTokenExpired: true
          });
        }
        // Clear queue since we can't process it
        this.failedSaveQueue = [];
        this.saveFailedQueueToStorage();
        return;
      }
      
      const queue = [...this.failedSaveQueue];
      this.failedSaveQueue = [];
      
      for (const item of queue) {
        // Check if response still exists and has changed
        const latestResponses = this.getLatestResponses();
        const currentResponse = latestResponses[item.questionId];
        
        if (!currentResponse) {
          logger.debug('Skipping queued save for question: Response no longer exists', { questionId: item.questionId });
          continue;
        }
        
        if (!this.hasResponseChanged(item.questionId, currentResponse)) {
          logger.debug('Skipping queued save for question: Already saved', { questionId: item.questionId });
          continue;
        }
        
        // Retry save
        const result = await this.saveResponse(item.questionId, currentResponse, {
          retryCount: item.retryCount,
          maxRetries: 2 // Fewer retries for queued items
        });
        
        if (!result.success && !result.cancelled) {
          // Still failed, add back to queue
          item.retryCount++;
          if (item.retryCount < 5) { // Max 5 retries
            this.failedSaveQueue.push(item);
          } else {
            // Max retries reached - mark as permanently failed
            this.updateSaveStatus(item.questionId, 'failed', {
              message: 'Save failed after multiple retries. Please try saving manually.',
              permanent: true
            });
          }
        }
      }
      
      this.saveFailedQueueToStorage();
      
    } finally {
      this.isProcessingQueue = false;
    }
  }
  
  /**
   * Save failed queue to localStorage
   * FIX #10: Implements queue size limit and age-based cleanup
   */
  saveFailedQueueToStorage() {
    if (typeof window === 'undefined') return;
    
    try {
      // FIX #10: Enforce queue size limit - remove oldest entries if over limit
      if (this.failedSaveQueue.length > this.MAX_FAILED_QUEUE_SIZE) {
        // Sort by timestamp (oldest first) and keep only the newest entries
        this.failedSaveQueue.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        const removed = this.failedSaveQueue.splice(0, this.failedSaveQueue.length - this.MAX_FAILED_QUEUE_SIZE);
        logger.warn('Failed queue exceeded limit, removed oldest entries', {
          removed: removed.length,
          remaining: this.failedSaveQueue.length
        });
      }
      
      const key = `quiz_failed_saves_${this.attemptId}`;
      const dataToStore = JSON.stringify(this.failedSaveQueue);
      
      // FIX #10: Check if data is too large (rough estimate: 5MB limit for localStorage)
      if (dataToStore.length > 4 * 1024 * 1024) { // 4MB safety limit
        logger.warn('Failed queue data too large, truncating', { size: dataToStore.length });
        // Keep only the most recent half
        this.failedSaveQueue = this.failedSaveQueue.slice(Math.floor(this.failedSaveQueue.length / 2));
        localStorage.setItem(key, JSON.stringify(this.failedSaveQueue));
      } else {
        localStorage.setItem(key, dataToStore);
      }
    } catch (error) {
      // FIX #10: Handle quota exceeded error specifically
      if (error.name === 'QuotaExceededError' || error.code === 22) {
        logger.warn('LocalStorage quota exceeded, clearing old quiz caches');
        this.clearOldQuizCaches();
        // Try again with reduced queue
        try {
          this.failedSaveQueue = this.failedSaveQueue.slice(-10); // Keep only last 10
          const key = `quiz_failed_saves_${this.attemptId}`;
          localStorage.setItem(key, JSON.stringify(this.failedSaveQueue));
        } catch (retryError) {
          logger.error('Failed to save queue even after cleanup', { error: retryError });
        }
      } else {
        logger.warn('Could not save failed queue to storage', { error });
      }
    }
  }
  
  /**
   * FIX #10: Clear old quiz caches to free up localStorage space
   */
  clearOldQuizCaches() {
    if (typeof window === 'undefined') return;
    
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('quiz_cache_') || key.startsWith('quiz_failed_saves_'))) {
          // Don't remove current attempt's data
          if (!key.includes(this.attemptId)) {
            keysToRemove.push(key);
          }
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      logger.debug('Cleared old quiz caches', { count: keysToRemove.length });
    } catch (error) {
      logger.warn('Failed to clear old quiz caches', { error });
    }
  }
  
  /**
   * Load failed queue from localStorage and IndexedDB
   * FIX #10: Implements age-based cleanup on load
   * RECOMMENDATION 4: Also loads from IndexedDB for better reliability
   */
  loadFailedQueueFromStorage() {
    if (typeof window === 'undefined') return;
    
    try {
      const key = `quiz_failed_saves_${this.attemptId}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        let queue = JSON.parse(stored);
        
        // FIX #10: Remove entries older than MAX_FAILED_QUEUE_AGE
        const now = Date.now();
        const originalLength = queue.length;
        queue = queue.filter(item => {
          const age = now - (item.timestamp || 0);
          return age < this.MAX_FAILED_QUEUE_AGE;
        });
        
        if (queue.length < originalLength) {
          logger.debug('Removed expired entries from failed queue', {
            removed: originalLength - queue.length,
            remaining: queue.length
          });
        }
        
        this.failedSaveQueue = queue;
        logger.debug('Loaded failed saves from localStorage', { count: this.failedSaveQueue.length });
        
        // Save back the cleaned queue
        if (queue.length < originalLength) {
          this.saveFailedQueueToStorage();
        }
      }
      
      // RECOMMENDATION 4: Also load from IndexedDB and merge
      this._loadFromIndexedDB();
      
    } catch (error) {
      logger.warn('Could not load failed queue from storage', { error });
      // FIX #10: Clear corrupted data
      try {
        const key = `quiz_failed_saves_${this.attemptId}`;
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore
      }
      
      // Still try to load from IndexedDB
      this._loadFromIndexedDB();
    }
  }
  
  /**
   * Load failed saves from IndexedDB and merge with existing queue
   * RECOMMENDATION 4: IndexedDB provides more reliable storage
   */
  async _loadFromIndexedDB() {
    try {
      const indexedDBSaves = await quizOfflineStorage.getFailedSaves(this.attemptId);
      
      if (indexedDBSaves.length > 0) {
        // Merge with existing queue, avoiding duplicates
        const existingQuestionIds = new Set(this.failedSaveQueue.map(item => item.questionId));
        
        for (const save of indexedDBSaves) {
          if (!existingQuestionIds.has(save.questionId)) {
            this.failedSaveQueue.push({
              questionId: save.questionId,
              response: save.response,
              attemptId: save.attemptId,
              timestamp: save.timestamp,
              retryCount: save.retryCount || 0,
              error: save.error
            });
            existingQuestionIds.add(save.questionId);
          }
        }
        
        logger.debug('Merged failed saves from IndexedDB', {
          indexedDBCount: indexedDBSaves.length,
          totalCount: this.failedSaveQueue.length
        });
      }
    } catch (error) {
      logger.warn('Could not load from IndexedDB', { error });
    }
  }
  
  /**
   * Clear failed queue
   * RECOMMENDATION 4: Also clears from IndexedDB
   */
  clearFailedQueue() {
    this.failedSaveQueue = [];
    if (typeof window !== 'undefined') {
      const key = `quiz_failed_saves_${this.attemptId}`;
      localStorage.removeItem(key);
      
      // RECOMMENDATION 4: Also clear from IndexedDB
      quizOfflineStorage.clearAttempt(this.attemptId)
        .catch(err => logger.warn('Failed to clear IndexedDB', { error: err }));
    }
  }
  
  /**
   * Debounced save (for auto-save)
   */
  debouncedSave(questionId, response, delay = 2000) {
    // Clear existing timer
    const existingTimer = this.debounceTimers.get(questionId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    
    // Set new timer
    const timer = setTimeout(() => {
      this.saveResponse(questionId, response);
      this.debounceTimers.delete(questionId);
    }, delay);
    
    this.debounceTimers.set(questionId, timer);
  }
  
  /**
   * Cancel all debounced saves
   */
  cancelAllDebouncedSaves() {
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer);
    }
    this.debounceTimers.clear();
  }
  
  /**
   * Check if error is token expiration
   * Improved detection: check status code and error message
   */
  isTokenExpirationError(error) {
    if (!error) return false;
    
    // Check status code first (most reliable)
    if (error.status === 401) {
      // Check error message to confirm it's token expiration (not other 401 errors)
      const message = error.message || error.toString() || '';
      return (
        message.includes('Token expired') ||
        message.includes('expired') ||
        message.includes('session has expired') ||
        message.includes('Session expired') ||
        message.includes('Invalid token') ||
        error.refreshTokenExpired === true
      );
    }
    
    // Check error message as fallback
    const message = error.message || error.toString() || '';
    return (
      message.includes('Token expired') ||
      message.includes('session has expired') ||
      message.includes('Session expired')
    );
  }
  
  /**
   * Set status change callback
   */
  setStatusChangeCallback(callback) {
    this.onStatusChange = callback;
  }
  
  /**
   * Set token refresh callback (deprecated - now uses tokenRefreshManager directly)
   * Kept for backward compatibility but does nothing
   */
  setTokenRefreshCallback(callback) {
    // No-op: token refresh is now handled by tokenRefreshManager
    // This method is kept to avoid breaking existing code
    logger.warn('setTokenRefreshCallback is deprecated. Token refresh is now handled automatically by tokenRefreshManager.');
  }
  
  /**
   * FIX #11: Set circuit breaker state change callback
   * Called when circuit breaker opens/closes
   */
  setCircuitBreakerCallback(callback) {
    this.onCircuitBreakerStateChange = callback;
  }
  
  /**
   * Set re-authentication required callback
   * Called when token expires and user needs to re-authenticate
   * For untimed quizzes, this shows a re-auth modal instead of auto-submitting
   * @param {Function} callback - Called with { savedAnswersCount, error }
   */
  setReAuthRequiredCallback(callback) {
    this.onReAuthRequired = callback;
  }
  
  /**
   * Set whether this is a timed quiz
   * Affects behavior when token expires:
   * - Timed quizzes: Continue with auto-submit behavior
   * - Untimed quizzes: Show re-auth modal, save locally, don't auto-submit
   * @param {boolean} isTimed - Whether the quiz has a time limit
   */
  setIsTimedQuiz(isTimed) {
    this.isTimedQuiz = !!isTimed;
    logger.debug('Quiz timed status set', { isTimedQuiz: this.isTimedQuiz });
  }
  
  /**
   * Trigger re-authentication required callback
   * Saves all pending responses locally and notifies UI
   * @param {Error} error - The error that triggered re-auth
   */
  async triggerReAuthRequired(error) {
    // Save all pending responses to local storage
    const latestResponses = this.getLatestResponses();
    const savedCount = Object.keys(latestResponses).length;
    
    // Save to IndexedDB/localStorage for persistence
    try {
      await quizOfflineStorage.saveAllResponses(this.attemptId, latestResponses);
      logger.info('Saved all responses locally for re-authentication', {
        attemptId: this.attemptId,
        savedCount
      });
    } catch (saveError) {
      logger.error('Failed to save responses locally', { error: saveError });
    }
    
    // Notify UI to show re-auth modal
    if (this.onReAuthRequired) {
      this.onReAuthRequired({
        savedAnswersCount: savedCount,
        error: error?.message || 'Session expired',
        attemptId: this.attemptId
      });
    }
  }
  
  /**
   * FIX #11: Manually reset the circuit breaker
   * Allows users to retry immediately instead of waiting for timeout
   */
  resetCircuitBreaker() {
    logger.info('Circuit breaker manually reset');
    this.tokenRefreshFailures = 0;
    this.tokenRefreshFailureWindow = null;
    
    // Notify UI
    if (this.onCircuitBreakerStateChange) {
      this.onCircuitBreakerStateChange({
        isOpen: false,
        failures: 0,
        timeRemaining: 0,
        manualReset: true
      });
    }
    
    // Process any queued saves
    this.processFailedQueue();
  }
  
  /**
   * FIX #11: Get circuit breaker status
   */
  getCircuitBreakerStatus() {
    const now = Date.now();
    const isOpen = this.tokenRefreshFailureWindow &&
                   (now - this.tokenRefreshFailureWindow) <= this.CIRCUIT_BREAKER_RESET_TIME &&
                   this.tokenRefreshFailures >= this.CIRCUIT_BREAKER_THRESHOLD;
    
    const timeRemaining = isOpen
      ? Math.ceil((this.CIRCUIT_BREAKER_RESET_TIME - (now - this.tokenRefreshFailureWindow)) / 1000)
      : 0;
    
    return {
      isOpen,
      failures: this.tokenRefreshFailures,
      threshold: this.CIRCUIT_BREAKER_THRESHOLD,
      timeRemaining
    };
  }
  
  /**
   * Check if there are any active saves in progress
   */
  hasActiveSaves() {
    return this.saveLock || this.inFlightRequests.size > 0;
  }
  
  /**
   * Check if a specific question has an active save
   */
  hasActiveSave(questionId) {
    return this.inFlightRequests.has(questionId) || 
           this.savesWaitingForRefresh.has(questionId) ||
           (this.saveStatus.get(questionId)?.status === 'saving');
  }
  
  /**
   * Wait for all active saves to complete
   * @param {number} timeout - Maximum time to wait in milliseconds (default: 5000ms)
   * @returns {Promise<boolean>} - true if all saves completed, false if timeout
   */
  async waitForSaves(timeout = 5000) {
    const startTime = Date.now();
    
    while (this.hasActiveSaves()) {
      if (Date.now() - startTime > timeout) {
        logger.warn('Timeout waiting for saves to complete', { timeout });
        return false;
      }
      // Wait a bit before checking again
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return true;
  }
  
  /**
   * Enhance error message based on error type
   */
  enhanceErrorMessage(error) {
    if (!error) return error;
    
    // Check if it's a network error
    if (error.name === 'AbortError' || error.message?.includes('timeout') || error.isTimeout) {
      const networkError = new Error('No internet connection. Your answer will be saved when connection returns.');
      networkError.isNetworkError = true;
      networkError.retryable = true;
      return networkError;
    }
    
    // Check if it's a 503 Service Unavailable error
    if (error.status === 503) {
      const serviceUnavailableError = new Error('Service temporarily unavailable (503). The server is overloaded or down. Your answer will be saved when the service is available.');
      serviceUnavailableError.isServiceUnavailable = true;
      serviceUnavailableError.isServerError = true;
      serviceUnavailableError.retryable = true;
      return serviceUnavailableError;
    }
    
    // Check if it's a server error (500)
    if (error.status === 500) {
      const serverError = new Error('Server error (500). Please try again in a moment.');
      serverError.isServerError = true;
      serverError.retryable = true;
      return serverError;
    }
    
    // Check if it's a rate limit error (429)
    if (error.status === 429 || error.rateLimited) {
      const rateLimitError = new Error('Too many requests. Please wait a moment and try again.');
      rateLimitError.isRateLimitError = true;
      rateLimitError.retryable = true;
      return rateLimitError;
    }
    
    // Check if it's an authentication error (401)
    if (error.status === 401 || error.refreshTokenExpired || this.isTokenExpirationError(error)) {
      const authError = new Error('Session expired. Please refresh the page and log in again.');
      authError.isAuthError = true;
      authError.refreshTokenExpired = error.refreshTokenExpired || false;
      return authError;
    }
    
    // Check if it's a network fetch error (no response)
    if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
      const networkError = new Error('Network error. Check your internet connection.');
      networkError.isNetworkError = true;
      networkError.retryable = true;
      return networkError;
    }
    
    // Return original error if no specific type detected
    return error;
  }
  
  /**
   * Cleanup
   */
  cleanup() {
    // Cancel all in-flight requests
    for (const controller of this.inFlightRequests.values()) {
      controller.abort();
    }
    this.inFlightRequests.clear();
    
    // Cancel all debounced saves
    this.cancelAllDebouncedSaves();
    
    // Clear saves waiting for refresh
    this.savesWaitingForRefresh.clear();
    
    // RECOMMENDATION 1: Clear token refresh retry counts
    this.tokenRefreshRetryCount.clear();
    
    // RECOMMENDATION 6: Reset circuit breaker
    this.tokenRefreshFailures = 0;
    this.tokenRefreshFailureWindow = null;
    
    // RECOMMENDATION 2: Stop periodic sync
    this.stopPeriodicSync();
    
    // Clear timers
    if (typeof window !== 'undefined') {
      if (this.handleOnline) {
        window.removeEventListener('online', this.handleOnline);
        this.handleOnline = null;
      }
    }
  }
}

export default QuizSaveManager;
